import React, { useState, useRef, useCallback, useEffect } from 'react';
import * as XLSX from 'xlsx';

function detectDelimiter(line = '') {
  const candidates = [',', ';', '\t', '|'];
  let best = ',';
  let bestCount = -1;
  candidates.forEach(d => {
    const count = (line.match(new RegExp(`\\${d}`, 'g')) || []).length;
    if (count > bestCount) {
      best = d;
      bestCount = count;
    }
  });
  return best;
}

function parseDelimitedLine(line, delimiter) {
  const out = [];
  let cur = '';
  let inQuotes = false;

  for (let i = 0; i < line.length; i += 1) {
    const ch = line[i];
    if (ch === '"') {
      if (inQuotes && line[i + 1] === '"') {
        cur += '"';
        i += 1;
      } else {
        inQuotes = !inQuotes;
      }
      continue;
    }

    if (ch === delimiter && !inQuotes) {
      out.push(cur.trim());
      cur = '';
      continue;
    }

    cur += ch;
  }

  out.push(cur.trim());
  return out.map(cell => cell.replace(/^"|"$/g, ''));
}

function scoreHeaderRow(cells = []) {
  if (!cells.length) return -Infinity;
  const normalized = cells
    .map(c => String(c || '').trim())
    .filter(Boolean);

  if (!normalized.length) return -Infinity;

  let score = 0;
  if (normalized.length >= 2) score += 4;
  if (normalized.length >= 3) score += 4;

  normalized.forEach(v => {
    const l = v.toLowerCase();
    if (l.includes('ticker') || l.includes('symbol') || l === 'stock') score += 8;
    if (l.includes('type') || l.includes('action') || l.includes('transaction')) score += 6;
    if (l.includes('date') || l.includes('time')) score += 6;
    if (l.includes('amount') || l.includes('value') || l.includes('notional') || l.includes('price')) score += 6;

    // Penalize likely title rows
    if (v.length > 60 && normalized.length <= 2) score -= 6;
  });

  return score;
}

function findHeaderRowIndex(rows2d = []) {
  if (!rows2d.length) return -1;
  const limit = Math.min(rows2d.length, 25);
  let bestIdx = 0;
  let bestScore = -Infinity;

  for (let i = 0; i < limit; i += 1) {
    const row = (rows2d[i] || []).map(c => String(c || '').trim());
    const score = scoreHeaderRow(row);
    if (score > bestScore) {
      bestScore = score;
      bestIdx = i;
    }
  }

  return bestIdx;
}

function uniqueHeaders(headers = []) {
  const seen = new Map();
  return headers.map((h, idx) => {
    const base = String(h || '').trim() || `Column ${idx + 1}`;
    const count = seen.get(base) || 0;
    seen.set(base, count + 1);
    return count === 0 ? base : `${base} (${count + 1})`;
  });
}

function toInputDate(value) {
  const raw = String(value || '').trim();
  if (!raw) return '';

  // Already YYYY-MM-DD (possibly with time)
  const isoPrefix = raw.match(/^(\d{4})-(\d{1,2})-(\d{1,2})/);
  if (isoPrefix) {
    const y = isoPrefix[1];
    const m = isoPrefix[2].padStart(2, '0');
    const d = isoPrefix[3].padStart(2, '0');
    return `${y}-${m}-${d}`;
  }

  // MM/DD/YYYY or M/D/YY
  const us = raw.match(/^(\d{1,2})[\/.-](\d{1,2})[\/.-](\d{2,4})$/);
  if (us) {
    let y = us[3];
    if (y.length === 2) y = `20${y}`;
    const m = us[1].padStart(2, '0');
    const d = us[2].padStart(2, '0');
    return `${y}-${m}-${d}`;
  }

  // Last resort: Date parser
  const dt = new Date(raw);
  if (!Number.isNaN(dt.getTime())) {
    const y = dt.getFullYear();
    const m = String(dt.getMonth() + 1).padStart(2, '0');
    const d = String(dt.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
  }

  return '';
}

// ── ImportModal ───────────────────────────────────────────────────────────────
export default function ImportModal({ onImport, onClose }) {
  const [step, setStep] = useState('upload'); // 'upload' | 'map'
  const [rawHeaders, setRawHeaders] = useState([]);
  const [rawRows, setRawRows] = useState([]);
  const [mapping, setMapping] = useState({
    ticker: '', type: '', date: '', amount: ''
  });
  const [preview, setPreview] = useState([]);
  const fileInputRef = useRef(null);

  const TARGETS = ['ticker', 'type', 'date', 'amount'];
  const TARGET_LABELS = {
    ticker: 'Ticker',
    type: 'Type (Buy/Sell)',
    date: 'Date',
    amount: 'Amount (USD)',
  };

  const handleFile = useCallback(async (file) => {
    if (!file) return;
    const ext = file.name.split('.').pop().toLowerCase();

    let rows = [];
    let headers = [];

    if (ext === 'csv') {
      const text = await file.text();
      const normalized = text.replace(/^\uFEFF/, '').trim();
      const linesRaw = normalized.split(/\r\n|\n|\r/).filter(Boolean);
      if (linesRaw.length < 1) return;

      const delimiter = detectDelimiter(linesRaw[0]);
      const lines = linesRaw.map(l => parseDelimitedLine(l, delimiter));
      const headerIdx = findHeaderRowIndex(lines);
      if (headerIdx < 0) return;

      headers = uniqueHeaders(lines[headerIdx] || []);
      rows = lines.slice(headerIdx + 1).map(cells => {
        const obj = {};
        headers.forEach((h, i) => { obj[h] = cells[i] || ''; });
        return obj;
      });
    } else {
      const buf = await file.arrayBuffer();
      const wb = XLSX.read(buf, { type: 'array' });
      const ws = wb.Sheets[wb.SheetNames[0]];
      const data = XLSX.utils.sheet_to_json(ws, { header: 1, defval: '' });
      if (data.length < 1) return;
      const normalizedRows = data.map(r => (r || []).map(c => String(c || '').trim()));
      const headerIdx = findHeaderRowIndex(normalizedRows);
      if (headerIdx < 0) return;

      headers = uniqueHeaders((normalizedRows[headerIdx] || []).map(String));
      rows = normalizedRows.slice(headerIdx + 1).map(cells => {
        const obj = {};
        headers.forEach((h, i) => { obj[h] = cells[i] != null ? String(cells[i]) : ''; });
        return obj;
      });
    }

    setRawHeaders(headers);
    setRawRows(rows);

    // Auto-detect mapping
    const autoMap = { ticker: '', type: '', date: '', amount: '' };
    const lower = h => h.toLowerCase();
    headers.forEach(h => {
      const l = lower(h);
      if (!autoMap.ticker && (l.includes('tick') || l === 'symbol' || l === 'stock')) autoMap.ticker = h;
      if (!autoMap.type  && (l.includes('type') || l.includes('buy') || l.includes('sell') || l.includes('action') || l.includes('transact'))) autoMap.type = h;
      if (!autoMap.date  && (l.includes('date') || l.includes('time'))) autoMap.date = h;
      if (!autoMap.amount && (l.includes('amount') || l.includes('value') || l.includes('notional') || l.includes('price') || l.includes('dollar'))) autoMap.amount = h;
    });
    setMapping(autoMap);
    setStep('map');
  }, []);

  // Update preview whenever mapping changes
  useEffect(() => {
    if (!rawRows.length) return;
    const mapped = rawRows.slice(0, 5).map(row => ({
      ticker: row[mapping.ticker] || '',
      type:   row[mapping.type]   || '',
      date:   row[mapping.date]   || '',
      amount: row[mapping.amount] || '',
    }));
    setPreview(mapped);
  }, [mapping, rawRows]);

  const handleConfirm = () => {
    const imported = rawRows
      .filter(row => row[mapping.ticker]?.trim())
      .map(row => ({
        ticker: (row[mapping.ticker] || '').trim().toUpperCase(),
        type:   (row[mapping.type]   || '').trim().toLowerCase(),
        date:   toInputDate(row[mapping.date]),
        amount: (row[mapping.amount] || '').trim(),
      }));
    onImport(imported);
    onClose();
  };

  return (
    <div className="stonks-modal-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="stonks-modal">
        <div className="stonks-modal-header">
          <span className="stonks-modal-title">
            {step === 'upload' ? '📥 Import File' : '🗂 Map Columns'}
          </span>
          <button className="btn-stonks btn-stonks-ghost" onClick={onClose} style={{ padding: '4px 8px' }}>✕</button>
        </div>

        <div className="stonks-modal-body">
          {step === 'upload' && (
            <>
              <p style={{ fontSize: 12, color: 'var(--s-text-dim)' }}>
                Upload a <strong>.csv</strong> or <strong>.xlsx</strong> file. You'll be able to map columns in the next step.
              </p>
              <div
                style={{
                  border: '2px dashed var(--s-border)',
                  borderRadius: 'var(--s-radius)',
                  padding: '32px 20px',
                  textAlign: 'center',
                  cursor: 'pointer',
                  transition: 'border-color 0.15s',
                }}
                onClick={() => fileInputRef.current?.click()}
                onDragOver={e => e.preventDefault()}
                onDrop={e => {
                  e.preventDefault();
                  handleFile(e.dataTransfer.files[0]);
                }}
              >
                <div style={{ fontSize: 32, marginBottom: 8 }}>📄</div>
                <div style={{ fontSize: 13, color: 'var(--s-text-dim)' }}>
                  Click to browse or drag & drop your file here
                </div>
                <div style={{ fontSize: 11, color: 'var(--s-text-muted)', marginTop: 4 }}>
                  Supports .csv and .xlsx
                </div>
              </div>
              <input
                ref={fileInputRef}
                type="file"
                accept=".csv,.xlsx,.xls"
                style={{ display: 'none' }}
                onChange={e => handleFile(e.target.files[0])}
              />
            </>
          )}

          {step === 'map' && (
            <>
              <p style={{ fontSize: 12, color: 'var(--s-text-dim)' }}>
                Map the columns from your file to the expected fields.
              </p>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                {TARGETS.map(target => (
                  <div key={target} style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                    <label style={{ fontSize: 11, color: 'var(--s-text-muted)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                      {TARGET_LABELS[target]}
                      {target === 'ticker' && <span style={{ color: 'var(--s-red)', marginLeft: 3 }}>*</span>}
                    </label>
                    <select
                      className="stonks-select"
                      value={mapping[target]}
                      onChange={e => setMapping(m => ({ ...m, [target]: e.target.value }))}
                      style={{ padding: '5px 24px 5px 8px', fontSize: 12 }}
                    >
                      <option value="">— {target === 'ticker' ? 'Required' : 'Skip'} —</option>
                      {rawHeaders.map(h => (
                        <option key={h} value={h}>{h}</option>
                      ))}
                    </select>
                  </div>
                ))}
              </div>

              {preview.length > 0 && (
                <div>
                  <div style={{ fontSize: 11, color: 'var(--s-text-muted)', marginBottom: 6, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                    Preview (first {preview.length} rows)
                  </div>
                  <div style={{ overflowX: 'auto' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                      <thead>
                        <tr>
                          {TARGETS.map(t => (
                            <th key={t} style={{ padding: '5px 8px', background: 'var(--s-surface-2)', color: 'var(--s-text-muted)', textAlign: 'left', borderBottom: '1px solid var(--s-border)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.4px', fontSize: 10 }}>
                              {TARGET_LABELS[t]}
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {preview.map((row, i) => (
                          <tr key={i}>
                            {TARGETS.map(t => (
                              <td key={t} style={{ padding: '5px 8px', borderBottom: '1px solid var(--s-border-subtle)', color: row[t] ? 'var(--s-text)' : 'var(--s-text-muted)', fontFamily: t === 'ticker' ? 'var(--s-mono)' : 'var(--s-font)' }}>
                                {row[t] || '—'}
                              </td>
                            ))}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </>
          )}
        </div>

        <div className="stonks-modal-footer">
          {step === 'map' && (
            <button className="btn-stonks btn-stonks-secondary" onClick={() => setStep('upload')}>
              ← Back
            </button>
          )}
          <button className="btn-stonks btn-stonks-ghost" onClick={onClose}>Cancel</button>
          {step === 'map' && (
            <button
              className="btn-stonks btn-stonks-primary"
              onClick={handleConfirm}
              disabled={!mapping.ticker}
            >
              Import {rawRows.length} rows
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
